import React, {useEffect, useMemo, useState} from 'react';
import API from "../utils/API";
import {API_ENDPOINTS} from "../apiConfig";
import Forms from "./Forms";
import Error from "./Error";

const PartMasterDuplicateModal = ({close, onSaved, part}) => {
    const [devices, setDevices] = useState([]);
    const [categories, setCategories] = useState([]);
    const [targets, setTargets] = useState([]);
    const [deviceToAdd, setDeviceToAdd] = useState(null);
    const [error, setError] = useState(null);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        (async () => {
            try {
                const [deviceRes, categoryRes] = await Promise.all([
                    API.get(API_ENDPOINTS.device.list),
                    API.get(API_ENDPOINTS.part.categories)
                ]);
                setDevices((deviceRes.data || []).map((item) => ({value: item._id, label: item.name})));
                setCategories((categoryRes.data || []).map((item) => ({value: item._id, label: item.name, code: item.code})));
            } catch (e) {
                setError(e);
            }
        })();
    }, []);

    const sourceDeviceIds = useMemo(
        () => new Set((part?.compatibleWith || []).map((item) => String(item?._id || item)).filter(Boolean)),
        [part]
    );

    const usedDeviceIds = useMemo(() => new Set(targets.map((target) => String(target?.deviceId)).filter(Boolean)), [targets]);

    const addableDeviceOptions = useMemo(
        () => devices.filter((option) => !sourceDeviceIds.has(String(option.value)) && !usedDeviceIds.has(String(option.value))),
        [devices, sourceDeviceIds, usedDeviceIds]
    );

    const updateTarget = (index, patch) => {
        setTargets((prev) => prev.map((row, rowIndex) => rowIndex === index ? {...row, ...patch} : row));
    };

    const addTargetByOption = (selectedOption) => {
        if (!selectedOption?.value) return;
        const option = addableDeviceOptions.find((item) => String(item.value) === String(selectedOption.value));
        if (!option) return;
        setTargets((prev) => [...prev, {
            deviceId: option.value,
            deviceLabel: option.label,
            categoryList: []
        }]);
        setDeviceToAdd(null);
    };

    const removeTarget = (index) => setTargets((prev) => prev.filter((_, rowIndex) => rowIndex !== index));

    const submit = async (e) => {
        e.preventDefault();
        setError(null);
        const payloadTargets = targets
            .filter((target) => target?.deviceId)
            .map((target) => ({
                deviceId: target.deviceId,
                categoryList: (target.categoryList || []).map((item) => item.value)
            }));

        if (!payloadTargets.length) {
            setError(new Error('Vyber alespoň jedno zařízení.'));
            return;
        }

        setSaving(true);
        try {
            await API.post(API_ENDPOINTS.part.duplicate(part._id), {targets: payloadTargets});
            await onSaved?.();
            close();
        } catch (e) {
            setError(e);
        } finally {
            setSaving(false);
        }
    };

    return (
        <form onSubmit={submit}>
            <div className="grid grid-cols-12 gap-2 items-end mb-3">
                <div className="col-span-12">
                    <Forms.Select
                        label="Přidat zařízení"
                        value={deviceToAdd}
                        options={addableDeviceOptions}
                        onChange={(value) => {
                            if (!value) {
                                setDeviceToAdd(null);
                                return;
                            }
                            addTargetByOption(value);
                        }}
                    />
                </div>
            </div>

            <div className="space-y-3">
                {targets.map((target, index) => (
                    <div key={`target_${index}`} className="grid grid-cols-12 gap-2 items-end border rounded p-3">
                        <div className="col-span-5">
                            <Forms.Text label="Zařízení" value={target.deviceLabel} onChange={() => {}} disabled/>
                        </div>
                        <div className="col-span-6">
                            <Forms.Select
                                label="Kategorie"
                                multiple
                                value={target.categoryList}
                                options={categories}
                                onChange={(value) => updateTarget(index, {categoryList: value || []})}
                            />
                        </div>
                        <div className="col-span-1">
                            <Forms.Button
                                type="button"
                                label="X"
                                className="bg-red-600 hover:bg-red-700"
                                onClick={() => removeTarget(index)}
                            />
                        </div>
                    </div>
                ))}
            </div>

            <div className="mt-4 flex items-center gap-2">
                <Forms.Button submit label={saving ? 'Ukládám...' : 'Duplikovat'} disabled={saving}/>
            </div>

            {error ? <Error error={error}/> : null}
        </form>
    );
};

export default PartMasterDuplicateModal;
